// src/components/equipements/FiltresEquipements.tsx — SCE Issues
// Filtres de la page Équipements (05/10), faits des pièces déjà en place
// ailleurs, pour que rien ne soit nouveau à l'œil :
//
//  - la barre de raccourcis du tableau de bord (`dashboard/BarreFiltres`) :
//    pastilles noires quand elles filtrent, « Tous les filtres (n) »,
//    « Réinitialiser » souligné ; une ligne qui défile au téléphone ;
//  - le panneau d'une colonne du tableau « Tous les problèmes »
//    (`issues/tableau/PopoverFiltre`) : cases à cocher et nombre par valeur ;
//  - la feuille de filtres du téléphone (`issues/tableau/FeuilleFiltres`) :
//    bulles jaunes, « Voir N équipements ».
//
// Toute la logique (critères, comptage) est dans `utils/filtresEquipements`.

import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, SlidersHorizontal, Wrench } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'
import { Sheet } from '@/components/ui/Sheet'
import { Button } from '@/components/ui/Button'
import { BulleFiltre } from '@/components/issues/tableau/FeuilleFiltres'
import {
  CRITERES,
  CRITERE_FABRICATION,
  EN_FABRICATION,
  LIBELLES_SECTIONS,
  RACCOURCIS,
  basculer,
  compterValeurs,
  critere,
  effacer,
  filtrer,
  nbCriteresActifs,
  valeursPossibles,
  type EquipementLu,
  type SectionCritere,
  type SelectionFiltres,
} from '@/utils/filtresEquipements'

const SEUIL_RECHERCHE = 8

export interface FiltresEquipementsProps {
  lus: readonly EquipementLu[]
  selection: SelectionFiltres
  onSelection: (changer: (prev: SelectionFiltres) => SelectionFiltres) => void
  recherche: string
  estBureau: boolean
  /** Équipements affichés / total du référentiel. */
  affiches: number
  total: number
  /** Liste des dossiers de production (05/10) : nombre et date ; absente = pas de pastille. */
  fabrication?: { nb: number; le: string } | null
  /** Ce que « Réinitialiser » rétablit : « En fabrication » quand la liste existe (05/10). */
  parDefaut: SelectionFiltres
}

/** Même sélection, quel que soit l'ordre des critères et des valeurs. */
function memeSelection(a: SelectionFiltres, b: SelectionFiltres): boolean {
  const cle = (s: SelectionFiltres): string =>
    JSON.stringify(
      Object.entries(s)
        .filter(([, v]) => v.length > 0)
        .map(([k, v]) => [k, [...v].sort()])
        .sort(([x], [y]) => String(x).localeCompare(String(y))),
    )
  return cle(a) === cle(b)
}

function resume(nom: string, valeurs: readonly string[]): string {
  if (valeurs.length === 0) return nom
  if (valeurs.length === 1) return `${nom} : ${valeurs[0]}`
  return `${nom} : ${valeurs.length}`
}

function pluriel(n: number): string {
  return `${n} équipement${n > 1 ? 's' : ''}`
}

/** « · liste du 05/10 à 10:12 » ; rien si la date est illisible. */
function dateListe(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const jour = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return ` · liste du ${jour} à ${heure}`
}

// ─── Barre de raccourcis ───

export function BarreFiltresEquipements(p: FiltresEquipementsProps): JSX.Element {
  const [ouvert, setOuvert] = useState<{ id: string; ancre: HTMLElement } | null>(null)
  const [feuille, setFeuille] = useState(false)
  // La fabrication a sa pastille dans la barre : elle ne compte pas dans « Tous les filtres ».
  const autres = nbCriteresActifs(p.selection) - nbCriteresActifs(p.selection, [...RACCOURCIS, CRITERE_FABRICATION])
  const fabricationActive = (p.selection[CRITERE_FABRICATION] ?? []).join() === EN_FABRICATION
  const reinitialisable = !memeSelection(p.selection, p.parDefaut)

  const pastilles = RACCOURCIS.map((id) => {
    const c = critere(id)
    if (!c) return null
    const valeurs = p.selection[id] ?? []
    const actif = valeurs.length > 0
    return (
      <button
        key={id}
        type="button"
        aria-expanded={ouvert?.id === id}
        aria-haspopup="dialog"
        onClick={(e) => {
          const ancre = e.currentTarget
          setOuvert((o) => (o?.id === id ? null : { id, ancre }))
        }}
        className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
        style={{
          height: 40,
          padding: '0 10px 0 14px',
          borderRadius: 20,
          border: '1px solid var(--color-black)',
          backgroundColor: actif ? 'var(--color-black)' : 'var(--color-white)',
          color: actif ? 'var(--color-white)' : 'var(--color-black)',
          fontSize: 14,
          fontWeight: actif ? 500 : 400,
        }}
      >
        {resume(c.court ?? c.libelle, valeurs)}
        <ChevronDown size={16} strokeWidth={2} />
      </button>
    )
  })

  // Une bascule, pas un panneau : « En fabrication » seuls, ou tous.
  const pastilleFabrication = p.fabrication && p.fabrication.nb > 0 && (
    <button
      type="button"
      aria-pressed={fabricationActive}
      onClick={() =>
        p.onSelection((s) =>
          fabricationActive ? effacer(s, CRITERE_FABRICATION) : { ...s, [CRITERE_FABRICATION]: [EN_FABRICATION] },
        )
      }
      className="flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
      style={{
        height: 40,
        padding: '0 14px',
        borderRadius: 20,
        border: '1px solid var(--color-black)',
        backgroundColor: fabricationActive ? 'var(--color-black)' : 'var(--color-white)',
        color: fabricationActive ? 'var(--color-white)' : 'var(--color-black)',
        fontSize: 14,
        fontWeight: fabricationActive ? 500 : 400,
      }}
    >
      <Wrench size={16} strokeWidth={2} />
      En fabrication · {p.fabrication.nb}
    </button>
  )

  const boutonTiroir = (
    <button
      type="button"
      onClick={() => setFeuille(true)}
      className="flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
      style={{
        height: 40,
        padding: '0 14px',
        borderRadius: 20,
        border: 'none',
        backgroundColor: autres > 0 ? 'var(--color-black)' : 'var(--color-gray-5)',
        color: autres > 0 ? 'var(--color-white)' : 'var(--color-black)',
        fontSize: 14,
      }}
    >
      <SlidersHorizontal size={16} strokeWidth={2} />
      Tous les filtres{autres > 0 ? ` (${autres})` : ''}
    </button>
  )

  const boutonReinitialiser = (
    <button
      type="button"
      onClick={() => p.onSelection(() => p.parDefaut)}
      disabled={!reinitialisable}
      className="disabled:opacity-40 flex-shrink-0"
      style={{
        height: 44,
        padding: '0 10px',
        border: 'none',
        background: 'transparent',
        color: 'var(--color-black)',
        fontSize: 14,
        textDecoration: 'underline',
      }}
    >
      Réinitialiser
    </button>
  )

  const compteur = (
    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
      <b style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{p.affiches}</b> sur {pluriel(p.total)}
      {/* La date de la liste : on voit tout de suite si elle a vieilli. */}
      {p.fabrication && p.fabrication.nb > 0 && ` · ${p.fabrication.nb} en fabrication${dateListe(p.fabrication.le)}`}
    </span>
  )

  return (
    <div>
      {p.estBureau ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {pastilleFabrication}
            {pastilles}
            {boutonTiroir}
            <span className="flex-1" />
            {boutonReinitialiser}
          </div>
          <div style={{ marginTop: 6 }}>{compteur}</div>
        </>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto" style={{ margin: '0 -16px', padding: '0 16px 2px', scrollbarWidth: 'none' }}>
            {pastilleFabrication}
            {pastilles}
            {boutonTiroir}
          </div>
          <div className="flex items-center gap-2" style={{ marginTop: 2 }}>
            <span className="flex-1">{compteur}</span>
            {boutonReinitialiser}
          </div>
        </>
      )}

      {ouvert && (
        <PopoverCritere
          id={ouvert.id}
          ancre={ouvert.ancre}
          lus={p.lus}
          selection={p.selection}
          recherche={p.recherche}
          onSelection={p.onSelection}
          onClose={() => setOuvert(null)}
        />
      )}
      {/* Dans <body> : rendue ici, la feuille hériterait du plan de la barre
          collante (z-index 20) et passerait sous l'en-tête. */}
      {feuille &&
        createPortal(
          <FeuilleFiltresEquipements
            lus={p.lus}
            selection={p.selection}
            recherche={p.recherche}
            onSelection={p.onSelection}
            parDefaut={p.parDefaut}
            onClose={() => setFeuille(false)}
          />,
          document.body,
        )}
    </div>
  )
}

// ─── Panneau d'un raccourci ───

function PopoverCritere({
  id,
  ancre,
  lus,
  selection,
  recherche,
  onSelection,
  onClose,
}: {
  id: string
  ancre: HTMLElement
  lus: readonly EquipementLu[]
  selection: SelectionFiltres
  recherche: string
  onSelection: FiltresEquipementsProps['onSelection']
  onClose: () => void
}): JSX.Element | null {
  const [saisie, setSaisie] = useState('')
  const c = critere(id)
  const toutes = useMemo(() => valeursPossibles(lus, id, selection), [lus, id, selection])
  const nombres = useMemo(() => compterValeurs(lus, selection, recherche, id), [lus, selection, recherche, id])
  if (!c) return null
  const choisies = selection[id] ?? []
  const t = saisie.trim().toLowerCase()
  const visibles = t ? toutes.filter((v) => v.toLowerCase().includes(t)) : toutes

  return (
    <Popover ancre={ancre} onClose={onClose} titre={`Filtrer : ${c.libelle}`}>
      <div className="popover-entete">
        <span>{c.libelle}</span>
        <button type="button" onClick={() => onSelection((s) => effacer(s, id))} disabled={choisies.length === 0}>
          Effacer
        </button>
      </div>
      {toutes.length > SEUIL_RECHERCHE && (
        <input
          type="search"
          className="popover-recherche"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Chercher…"
          aria-label={`Chercher dans ${c.libelle}`}
          autoFocus
        />
      )}
      <div className="popover-liste">
        {visibles.length === 0 && <p className="popover-vide">Aucune valeur</p>}
        {visibles.map((v) => {
          const n = nombres.get(v) ?? 0
          return (
            <label key={v} className={n === 0 ? 'tableau-option tableau-option--vide' : 'tableau-option'}>
              <input type="checkbox" checked={choisies.includes(v)} onChange={() => onSelection((s) => basculer(s, id, v))} />
              <span className="tableau-option-libelle">{v}</span>
              <span className="tableau-option-nombre">{n}</span>
            </label>
          )
        })}
      </div>
      {c.section !== 'general' && (
        <div className="popover-pied">
          <span>{LIBELLES_SECTIONS[c.section]} seulement, lu dans le code de désignation.</span>
        </div>
      )}
    </Popover>
  )
}

// ─── Tous les filtres ───

const SECTIONS: readonly SectionCritere[] = ['general', 'colonne', 'hipersep']

const VIDE_SECTION: Record<Exclude<SectionCritere, 'general'>, string> = {
  colonne: 'Aucune colonne avec les filtres en cours.',
  hipersep: 'Aucun système Hipersep avec les filtres en cours.',
}

function FeuilleFiltresEquipements({
  lus,
  selection,
  recherche,
  onSelection,
  parDefaut,
  onClose,
}: {
  lus: readonly EquipementLu[]
  selection: SelectionFiltres
  recherche: string
  onSelection: FiltresEquipementsProps['onSelection']
  parDefaut: SelectionFiltres
  onClose: () => void
}): JSX.Element {
  const n = filtrer(lus, selection, recherche).length

  return (
    <Sheet
      titre="Tous les filtres"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={() => onSelection(() => parDefaut)}>
            Réinitialiser
          </Button>
          <Button variant="primary" fullWidth onClick={onClose}>
            Voir {pluriel(n)}
          </Button>
        </>
      }
    >
      {SECTIONS.map((section) => {
        const criteres = CRITERES.filter((c) => c.section === section)
        // Une section du code (colonnes, systèmes) n'a rien à proposer quand les
        // autres filtres n'en laissent aucun : une phrase plutôt que des zéros.
        const sansObjet =
          section !== 'general' &&
          nbCriteresActifs(selection, criteres.map((c) => c.id)) === 0 &&
          !filtrer(lus, selection, recherche).some((e) => e.famille === section)
        return (
          <section key={section} className="flex flex-col gap-4">
            <h3
              style={{
                fontSize: 'var(--font-size-copy2)',
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              {LIBELLES_SECTIONS[section]}
            </h3>
            {section !== 'general' && !sansObjet && (
              <p style={{ margin: '-8px 0 0', fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>
                Lu dans le code de désignation. « Non renseigné » : code absent ou incomplet dans Windchill.
              </p>
            )}
            {sansObjet ? (
              <p style={{ margin: 0, fontSize: 'var(--font-size-copy2)', color: 'var(--color-text-secondary)' }}>
                {VIDE_SECTION[section]}
              </p>
            ) : (
              criteres.map((c) => {
                const nombres = compterValeurs(lus, selection, recherche, c.id)
                const choisies = selection[c.id] ?? []
                const valeurs = valeursPossibles(lus, c.id, selection)
                if (valeurs.length === 0) return null
                return (
                  <div key={c.id}>
                    <h4 className="feuille-titre">{c.libelle}</h4>
                    <div className="flex flex-wrap gap-2">
                      {valeurs.map((v) => (
                        <BulleFiltre
                          key={v}
                          libelle={`${v} · ${nombres.get(v) ?? 0}`}
                          actif={choisies.includes(v)}
                          onClick={() => onSelection((s) => basculer(s, c.id, v))}
                        />
                      ))}
                    </div>
                  </div>
                )
              })
            )}
          </section>
        )
      })}
    </Sheet>
  )
}
